import React, { useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./CallPage.css";

const SERVER_WS = "https://be.saberalpha.online/ws/call/";

export default function CallPage() {
  // Get room name from URL parameters
  const { roomId } = useParams();
  const navigate = useNavigate();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnection = useRef(null);
  const webSocket = useRef(null);
  const localStreamRef = useRef(null);

  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [connected, setConnected] = useState(false);

  // Cleanup function to handle resources properly
  const cleanup = useCallback(() => {
    if (peerConnection.current) {
      peerConnection.current.onicecandidate = null;
      peerConnection.current.ontrack = null;
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (webSocket.current) {
      webSocket.current.onopen = null;
      webSocket.current.onmessage = null;
      webSocket.current.onclose = null;
      webSocket.current.close();
      webSocket.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
  }, []);

  // Send message helper
  const sendMessage = useCallback((message) => {
    if (webSocket.current && webSocket.current.readyState === WebSocket.OPEN) {
      webSocket.current.send(JSON.stringify(message));
    }
  }, []);

  // Main call setup function
  const startCall = useCallback(async () => {
    cleanup();

    try {
      // Get user media
      const localStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      localStreamRef.current = localStream;

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStream;
      }

      // Create RTCPeerConnection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
      peerConnection.current = pc;

      // Add tracks to peer connection
      localStream
        .getTracks()
        .forEach((track) => pc.addTrack(track, localStream));

      // Set up ICE candidate handling
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendMessage({ type: "ice-candidate", candidate: event.candidate });
        }
      };

      // Handle incoming tracks
      pc.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
      };

      // Create WebSocket connection
      const ws = new WebSocket(`${SERVER_WS}${roomId}/`);
      webSocket.current = ws;

      // Handle WebSocket opening
      ws.onopen = async () => {
        setConnected(true);
        // Only create an offer if we're the initiator (this logic might need adjustment based on your server implementation)
        if (pc.signalingState === "stable") {
          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            sendMessage({ type: "offer", offer });
          } catch (err) {
            console.error("Error creating offer:", err);
          }
        }
      };

      // Handle WebSocket messages
      ws.onmessage = async (event) => {
        if (!peerConnection.current) return;

        const data = JSON.parse(event.data);

        try {
          if (data.type === "offer") {
            await pc.setRemoteDescription(
              new RTCSessionDescription(data.offer)
            );
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            sendMessage({ type: "answer", answer });
          } else if (data.type === "answer") {
            await pc.setRemoteDescription(
              new RTCSessionDescription(data.answer)
            );
          } else if (data.type === "ice-candidate" && data.candidate) {
            await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
          }
        } catch (err) {
          console.error("Error handling signaling message:", err);
        }
      };

      // Handle WebSocket closing
      ws.onclose = () => {
        setConnected(false);
        console.log("WebSocket connection closed");
      };

      // Handle WebSocket errors
      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setConnected(false);
      };
    } catch (err) {
      console.error("Could not get media devices:", err);
      alert("Could not get media devices: " + err.message);
    }
  }, [cleanup, sendMessage, roomId]);

  // Initial setup
  useEffect(() => {
    startCall();
    // Cleanup when component unmounts
    return () => cleanup();
  }, [startCall, cleanup]);

  // Media controls
  const toggleMic = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !micOn;
      });
      setMicOn(!micOn);
    }
  };

  const toggleCam = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach((track) => {
        track.enabled = !camOn;
      });
      setCamOn(!camOn);
    }
  };

  const endCall = () => {
    cleanup();
    setConnected(false);
    // Navigate back to home page
    navigate("/");
  };

  return (
    <div className="call-container">
      {/* Room Info Bar */}
      <div className="room-info-bar">
        <div className="room-name">Room: {roomId}</div>
        <div className="connection-status" data-connected={connected}>
          {connected ? "Connected" : "Disconnected"}
        </div>
      </div>

      {/* Remote Video */}
      <div className="remote-video-container">
        <video
          ref={remoteVideoRef}
          className="remote-video"
          autoPlay
          playsInline
        />
      </div>

      {/* Local Video */}
      <div className="local-video-container">
        <video
          ref={localVideoRef}
          className="local-video"
          autoPlay
          muted
          playsInline
        />
      </div>

      {/* Controls */}
      <div className="call-controls">
        <button
          className={`control-button mic-button ${micOn ? "" : "off"}`}
          onClick={toggleMic}
        >
          {micOn ? "🎤" : "🔇"}
          <span className="button-label">{micOn ? "Mute" : "Unmute"}</span>
        </button>

        <button
          className={`control-button cam-button ${camOn ? "" : "off"}`}
          onClick={toggleCam}
        >
          {camOn ? "📷" : "❌"}
          <span className="button-label">
            {camOn ? "Stop Video" : "Start Video"}
          </span>
        </button>

        <button className="control-button end-call-button" onClick={endCall}>
          ⛔<span className="button-label">End Call</span>
        </button>

        {!connected && (
          <button
            className="control-button reconnect-button"
            onClick={startCall}
          >
            🔄
            <span className="button-label">Reconnect</span>
          </button>
        )}
      </div>
    </div>
  );
}
